import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useUser } from "@/redux/user";
import { api } from "@/services/api";
import { browserPracticeTimezone } from "../puzzles/dailyPractice";
import { LearningOverview } from "./learningTypes";
import { getGuestLearningOverview } from "./guestLearningProgress";

type Entry = {
  data: LearningOverview | null;
  error: boolean;
  updated: number;
  request?: Promise<void>;
  listeners: Set<() => void>;
};
const accounts = new Map<number, Entry>();
function account(id: number) {
  if (!accounts.has(id))
    accounts.set(id, {
      data: null,
      error: false,
      updated: 0,
      listeners: new Set(),
    });
  return accounts.get(id)!;
}
function fetchOverview(id: number, force = false) {
  const entry = account(id);
  if (entry.request) return entry.request;
  if (!force && entry.updated > Date.now() - 15000) return Promise.resolve();
  entry.request = api
    .get("/learning-overview", {
      params: { timezone: browserPracticeTimezone() },
      timeout: 15000,
    })
    .then(({ data }) => {
      entry.data = data;
      entry.error = false;
      entry.updated = Date.now();
    })
    .catch(() => {
      entry.error = true;
    })
    .finally(() => {
      entry.request = undefined;
      entry.listeners.forEach((notify) => notify());
    });
  return entry.request;
}

export default function useLearningOverview() {
  const { id, memberLoggedIn } = useUser();
  const { asPath } = useRouter();
  const [, render] = useState(0);
  const [guestData, setGuestData] = useState<LearningOverview | null>(null);
  const refresh = useCallback(() => {
    if (memberLoggedIn && id) void fetchOverview(id, true);
    else setGuestData(getGuestLearningOverview());
  }, [id, memberLoggedIn]);
  useEffect(() => {
    if (!memberLoggedIn || !id) {
      const reload = () => setGuestData(getGuestLearningOverview());
      const visible = () => {
        if (document.visibilityState === "visible") reload();
      };
      const stored = (event: StorageEvent) => {
        if (!event.key || event.key.startsWith("chesshotel:")) reload();
      };
      reload();
      window.addEventListener("chess-activity", reload);
      window.addEventListener("focus", visible);
      window.addEventListener("storage", stored);
      document.addEventListener("visibilitychange", visible);
      const timer = window.setInterval(visible, 60000);
      return () => {
        window.removeEventListener("chess-activity", reload);
        window.removeEventListener("focus", visible);
        window.removeEventListener("storage", stored);
        document.removeEventListener("visibilitychange", visible);
        window.clearInterval(timer);
      };
    }
    setGuestData(null);
    const entry = account(id);
    const notify = () => render((n) => n + 1);
    const reload = () => {
      if (document.visibilityState === "visible") refresh();
    };
    entry.listeners.add(notify);
    void fetchOverview(id);
    window.addEventListener("chess-activity", refresh);
    window.addEventListener("focus", reload);
    document.addEventListener("visibilitychange", reload);
    // Covers local midnight and games archived after the result arrives.
    const timer = window.setInterval(reload, 60000);
    return () => {
      entry.listeners.delete(notify);
      if (!entry.listeners.size) accounts.delete(id);
      window.removeEventListener("chess-activity", refresh);
      window.removeEventListener("focus", reload);
      document.removeEventListener("visibilitychange", reload);
      window.clearInterval(timer);
    };
  }, [id, memberLoggedIn, refresh]);
  useEffect(() => {
    if (memberLoggedIn && id) void fetchOverview(id);
  }, [asPath, id, memberLoggedIn]);
  const entry = memberLoggedIn && id ? account(id) : null;
  return {
    data: memberLoggedIn ? entry?.data || null : guestData,
    error: memberLoggedIn ? entry?.error || false : false,
    refresh,
  };
}
